import api from './api.service';

export type MatchSource = 'qr' | 'barcode' | 'vision' | 'tag' | 'manual_correction';

export type RecognitionCandidate = {
  itemId: string;
  itemCode: string;
  itemName: string;
  confidence: number;
  signals: { name: string; score: number }[];
};

export type RecognitionResult = {
  attemptId: string;
  matchSource: MatchSource | null;
  confidence: number;
  selectedItem?: {
    id: string;
    itemCode: string;
    itemName: string;
    brand?: string | null;
    imageUrl?: string | null;
  };
  candidates: RecognitionCandidate[];
  coaching?: string;
  failureReason?: string;
};

export type ReferenceImage = {
  id: string;
  viewType: string;
  imageUrl: string;
  sortOrder: number;
  isPrimary: boolean;
};

export type RecognitionAnalyticsSummary = {
  totalAttempts: number;
  successRate: number;
  avgConfidence: number | null;
  avgLatencyMs: number | null;
  lowConfidenceCount: number;
  providerPerformance: {
    providerModel: string;
    attempts: number;
    avgLatencyMs: number | null;
    errors: number;
  }[];
  thresholds: { autoSelect: number; confirm: number };
};

function unwrap<T>(res: unknown): T {
  const body = (res as { data?: unknown })?.data ?? res;
  return ((body as { data?: T })?.data ?? body) as T;
}

export async function runRecognition(file: File, source: 'mobile' | 'admin_lab' = 'admin_lab'): Promise<RecognitionResult> {
  const form = new FormData();
  form.append('image', file);
  form.append('source', source);
  const res = await api.getClient().post('/product-recognition/lab/run', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap<RecognitionResult>(res);
}

export async function submitRecognitionFeedback(payload: {
  attemptId: string;
  correctedItemId: string;
  notes?: string;
  source?: 'mobile' | 'admin_lab';
}): Promise<{ feedbackId: string; appliedTagUpdates: string[] }> {
  const res = await api.getClient().post('/product-recognition/lab/feedback', {
    ...payload,
    source: payload.source || 'admin_lab',
  });
  return unwrap(res);
}

export async function listReferenceImages(itemId: string): Promise<ReferenceImage[]> {
  const res = await api.getClient().get(`/product-recognition/items/${itemId}/images`);
  return unwrap<ReferenceImage[]>(res);
}

export async function uploadReferenceImage(
  itemId: string,
  file: File,
  viewType: string,
  isPrimary?: boolean
): Promise<ReferenceImage> {
  const form = new FormData();
  form.append('image', file);
  form.append('viewType', viewType);
  if (isPrimary) form.append('isPrimary', 'true');
  const res = await api.getClient().post(`/product-recognition/items/${itemId}/images`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap<ReferenceImage>(res);
}

export async function deleteReferenceImage(itemId: string, imageId: string): Promise<void> {
  await api.getClient().delete(`/product-recognition/items/${itemId}/images/${imageId}`);
}

export async function getItemTags(itemId: string): Promise<{
  tags: { id: string; tag: string; source: string; weight: number }[];
  aliases: { id: string; aliasText: string; source: string }[];
}> {
  const res = await api.getClient().get(`/product-recognition/items/${itemId}/tags`);
  return unwrap(res);
}

export async function updateItemTags(
  itemId: string,
  payload: { tags?: string[]; aliases?: string[] }
): Promise<unknown> {
  const res = await api.getClient().patch(`/product-recognition/items/${itemId}/tags`, payload);
  return unwrap(res);
}

export async function fetchRecognitionSummary(params?: { from?: string; to?: string }): Promise<RecognitionAnalyticsSummary> {
  const res = await api.getClient().get('/product-recognition/analytics/summary', { params });
  return unwrap<RecognitionAnalyticsSummary>(res);
}

export async function fetchRecognitionAttempts(params: {
  page?: number;
  pageSize?: number;
  from?: string;
  to?: string;
}): Promise<{ data: Record<string, unknown>[]; total: number; page: number; pageSize: number }> {
  const res = await api.getClient().get('/product-recognition/analytics/attempts', { params });
  return unwrap(res);
}

export async function fetchConfusionMatrix(limit = 20): Promise<Record<string, unknown>[]> {
  const res = await api.getClient().get('/product-recognition/analytics/confusion', { params: { limit } });
  return unwrap<Record<string, unknown>[]>(res);
}
